import React from 'react';
import { Box, IconButton, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { AgencyIcon } from '../AgencyIcon';
import { GreeningIcon } from './GreeningIcon';

/** Numeral fixo de exemplo — padrão visual Q&A AI Greening (filtro, não contagem dinâmica). */
const GREENING_FILTER_SAMPLE_NUMERAL = '2';

interface Props {
  monitoredNormAssuntoActive: boolean;
  monitoredAgencyActive: boolean;
  greeningQaFilterActive: boolean;
  onMonitoredNormAssuntoToggle: () => void;
  onMonitoredAgencyToggle: () => void;
  onGreeningQaFilterToggle: () => void;
}

export function ChangesActionsColumnHeader({
  monitoredNormAssuntoActive,
  monitoredAgencyActive,
  greeningQaFilterActive,
  onMonitoredNormAssuntoToggle,
  onMonitoredAgencyToggle,
  onGreeningQaFilterToggle,
}: Props) {
  const theme = useTheme();
  const disabledColor = theme.palette.text.disabled;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Box component="strong" sx={{ fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.2 }}>
        Ações
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        <Tooltip title="Filtrar assuntos e palavras-chave monitorados (período da tabela)" arrow>
          <IconButton
            size="small"
            aria-pressed={monitoredNormAssuntoActive}
            aria-label="Filtrar assuntos e palavras-chave monitorados"
            onClick={(e) => {
              e.stopPropagation();
              onMonitoredNormAssuntoToggle();
            }}
            sx={{
              p: 0.375,
              color: monitoredNormAssuntoActive ? 'info.main' : disabledColor,
              bgcolor: monitoredNormAssuntoActive ? alpha(theme.palette.info.main, 0.12) : 'transparent',
              '&:hover': {
                bgcolor: monitoredNormAssuntoActive
                  ? alpha(theme.palette.info.main, 0.2)
                  : alpha(theme.palette.action.hover, 0.08),
              },
            }}
          >
            <Visibility sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Filtrar agências monitoradas (período da tabela)" arrow>
          <IconButton
            size="small"
            aria-pressed={monitoredAgencyActive}
            aria-label="Filtrar agências monitoradas"
            onClick={(e) => {
              e.stopPropagation();
              onMonitoredAgencyToggle();
            }}
            sx={{
              p: 0.375,
              color: monitoredAgencyActive ? 'primary.main' : disabledColor,
              bgcolor: monitoredAgencyActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
              '&:hover': {
                bgcolor: monitoredAgencyActive
                  ? alpha(theme.palette.primary.main, 0.18)
                  : alpha(theme.palette.action.hover, 0.08),
              },
            }}
          >
            <AgencyIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Filtrar conversas com perguntas e respostas feitas pela GreeningAI" arrow>
          <IconButton
            size="small"
            aria-pressed={greeningQaFilterActive}
            aria-label="Filtrar conversas com perguntas e respostas feitas pela GreeningAI"
            onClick={(e) => {
              e.stopPropagation();
              onGreeningQaFilterToggle();
            }}
            sx={{
              p: 0.25,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              borderRadius: 1,
              color: greeningQaFilterActive ? 'success.main' : disabledColor,
              bgcolor: greeningQaFilterActive ? alpha(theme.palette.success.main, 0.1) : 'transparent',
              '&:hover': {
                bgcolor: greeningQaFilterActive
                  ? alpha(theme.palette.success.main, 0.16)
                  : alpha(theme.palette.action.hover, 0.08),
              },
            }}
          >
            <GreeningIcon
              sx={{
                fontSize: 20,
                color: greeningQaFilterActive ? undefined : disabledColor,
                opacity: greeningQaFilterActive ? 1 : 0.55,
              }}
            />
            <Typography
              component="span"
              variant="caption"
              fontWeight={700}
              sx={{
                fontSize: '0.7rem',
                lineHeight: 1,
                color: greeningQaFilterActive ? 'info.main' : disabledColor,
              }}
            >
              {GREENING_FILTER_SAMPLE_NUMERAL}
            </Typography>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
